import { memo } from 'react';
import Markdown from '@/components/markdown';
import AiReply from './ai-reply';
import SimilarQuestion from './similar-question';
import SubjectSearch from './subject-search';
import GoToTeacherQa from './go-to-teacher-qa';
import AudioTranslate from './audio-translate';
import QuestionOfOcrSearch from './question-of-ocr-search';
import GuessYouAsk from './guess-you-ask';
import QuestionSource from './question-source';
import EveryoneIsAsk from './everyone-is-ask';
import { ISimilarProblemItem, ISimilarProblem, ISubjectSearch, IEveryoneIsAskItemInOcr } from '@/types/models/qa';
import { getGlobalStore } from '@/store/global';
import { getQaStore } from '@/store/qa';
import { previewPicture } from '@/utils';
import classNames from 'classnames';
import './index.scss';

interface IRespondentBasic {
    role: 'respondent';
    recordId: string;
}
interface IWelcome extends IRespondentBasic {
    type: 'welcome';
    content: string;
}
export interface IAi extends IRespondentBasic {
    type: 'ai';
    content: string;
    feedbackType?: 1 | 2;
    isFeedbackEvaluation?: boolean;
    permanentActionBar?: boolean;
}
interface ITeacherReply extends IRespondentBasic {
    type: 'teacherReply';
    content: string;
}
interface IStreamTips extends IRespondentBasic {
    type: 'streamTips';
    content: string;
}
interface IQaNumUesdUpTips extends IRespondentBasic {
    type: 'qaNumUsedUp';
    content: string;
}
interface ISimilarQuestionCard extends IRespondentBasic {
    type: 'similarQuestion';
    list: ISimilarProblemItem[];
    dataSource: ISimilarProblem;
}
interface ISubjectSearchCard extends Required<ISubjectSearch>, IRespondentBasic {
    type: 'subjectSearch';
    dataSource: ISubjectSearch;
    sensorsData: {
        lucaAskChatId: string;
        recordId: string;
        qaNum: number;
        videoNum: number;
        knowledgePointsNum: number;
    };
}

interface IGotoTeacherQaTips extends IRespondentBasic {
    type: 'goToTeacherQaTips';
    content: string;
}
interface IGoToTeacherQaCard extends IRespondentBasic {
    type: 'goToTeacherQa';
    teacherAnswerTime: number | null;
}
interface IRetry extends IRespondentBasic {
    type: 'retry';
    question: string;
    retryType: 1 | 2;
}

interface IVoiceReply extends IRespondentBasic {
    type: 'voiceReply';
    params: {
        linkContent: string;
        linkTime: number;
        linkFile: string;
        audioKey: string;
    };
}
export interface IQuestionOfOcrSearch extends IRespondentBasic {
    type: 'questionOfOcrSearch';
    similarity: number;
    content: string;
    questionId: number;
    littleQuestionId?: number;
    hideReplaceOcrResultBtn?: boolean;
    everyAsks: IEveryoneIsAskItemInOcr[];
}

export interface IGuessYouLike extends IRespondentBasic {
    type: 'guessYouAsk';
    questions: string[];
    relateId: number;
}

export interface IQuestionSource extends IRespondentBasic {
    type: 'questionSource';
    sourceType: 'video' | 'question';
    title: string;
    content: string;
    videoId?: string;
    url?: string;
    videoTime?: number;
    questionId?: string;
    // 是否有权限查看视频
    permission: 0 | 1;
}

export interface IRecharge extends IRespondentBasic {
    type: 'rechargeLucaQa' | 'rechargeTeacherQa' | 'noPermissionOfProbeByAi' | 'noPermissionOfProbeByNonAi';
}

export interface IEveryoneIsAsk extends IRespondentBasic {
    type: 'everyoneIsAsk';
    asks: IEveryoneIsAskItemInOcr[];
    hasNextPage: boolean;
}
interface IQaPairReply extends IRespondentBasic {
    type: 'qaPairReply';
    content: string;
    isFlash?: boolean;
}

export type IRespondent =
    | IWelcome
    | IAi
    | ISimilarQuestionCard
    | IGoToTeacherQaCard
    | IGotoTeacherQaTips
    | IRetry
    | IStreamTips
    | ITeacherReply
    | IVoiceReply
    | IQaNumUesdUpTips
    | IQuestionOfOcrSearch
    | IGuessYouLike
    | IQuestionSource
    | IRecharge
    | IEveryoneIsAsk
    | IQaPairReply
    | ISubjectSearchCard;

const Respondent = (data: IRespondent) => {
    const handleRetry = (content: string, retryType: 1 | 2) => {
        const { updateQaConfig, validateCanSend, filterQaRecords } = getQaStore()!;
        if (!validateCanSend()) return;
        updateQaConfig({ showWhetherSolve: false, questionState: 0 });
        filterQaRecords({ retry: true, whetherSolve: true, goToTeacherQaTips: true, goToTeacherQa: true, guessYouAsk: true });
        getGlobalStore()?.publish('DialogueArea', { type: 'retry', content, retryType });
    };

    const handleClick = ev => {
        if (ev.target.nodeName !== 'IMG') return;
        previewPicture(ev.target.src);
    };

    const handleReCharge = () => {
        const { lucaId, projectId } = getQaStore()!.qaConfig;
        getGlobalStore()?.publish('RightsModal', { type: 'open', params: { lucaId, projectId } });
    };

    switch (data.type) {
        case 'ai':
            return <AiReply {...data} />;
        case 'qaPairReply':
            return (
                <div className={classNames('respondent-container', data.isFlash && 'respondent-container--flash')}>
                    <div className="respondent__normal">
                        <Markdown content={data.content} imgType="normal" />
                    </div>
                </div>
            );
        case 'welcome':
        case 'goToTeacherQaTips':
        case 'streamTips':
        case 'qaNumUsedUp':
            return (
                <div className="respondent-container">
                    <div className={data.type === 'welcome' ? 'respondent__welcome' : 'respondent__normal'}>
                        <Markdown content={data.content} imgType={data.type === 'welcome' ? 'welcome' : 'normal'} />
                    </div>
                </div>
            );
        case 'teacherReply':
            return (
                <div className="respondent-container">
                    <div className="respondent__teacher-reply">
                        <div
                            className="respondent__rich-text"
                            dangerouslySetInnerHTML={{ __html: data.content }}
                            onClick={handleClick}
                        ></div>
                    </div>
                </div>
            );
        case 'retry':
            return (
                <div className="respondent-container">
                    <div className="respondent__retry">
                        <span>服务好像出了点问题，请点击</span>
                        <span onClick={() => handleRetry(data.question, data.retryType)}>{' 重试 '}</span>
                        <span>。你也可以点击下方【未解决】按钮，将问题提交给名师回答</span>
                    </div>
                </div>
            );
        case 'noPermissionOfProbeByAi':
        case 'noPermissionOfProbeByNonAi':
            return (
                <div className="respondent-container">
                    <div className="respondent__recharge">
                        <span>暂无名师答疑权限，请先</span>
                        {data.type === 'noPermissionOfProbeByAi' ? (
                            <span className="respondent__recharge-btn" onClick={handleReCharge}>
                                购买答疑权益包
                            </span>
                        ) : (
                            <span>购买课程</span>
                        )}
                    </div>
                </div>
            );
        case 'rechargeLucaQa':
        case 'rechargeTeacherQa':
            return (
                <div className="respondent-container">
                    <div className="respondent__recharge">
                        {data.type === 'rechargeLucaQa' && <span>Luca答疑次数已用完，请</span>}
                        {data.type === 'rechargeTeacherQa' && <span>名师答疑次数已用完，请</span>}
                        <span className="respondent__recharge-btn" onClick={handleReCharge}>
                            购买答疑权益包
                        </span>
                        {data.type === 'rechargeLucaQa' && <span>。你也可以点击【未解决】按钮，将问题提交给名师回答</span>}
                    </div>
                </div>
            );
        case 'voiceReply': {
            const params = data.params;
            return (
                <div className="voice-container">
                    <AudioTranslate
                        audioKey={params.audioKey}
                        linkContent={params.linkContent}
                        linkTime={params.linkTime}
                        linkFile={params.linkFile}
                    />
                </div>
            );
        }
        case 'similarQuestion':
            return <SimilarQuestion list={data.list} />;
        case 'subjectSearch':
            return <SubjectSearch recordId={data.recordId} qaList={data.qaList} knowledgeSectionList={data.knowledgeSectionList} />;
        case 'goToTeacherQa':
            return <GoToTeacherQa teacherAnswerTime={data.teacherAnswerTime} />;
        case 'questionOfOcrSearch':
            return (
                <QuestionOfOcrSearch
                    recordId={data.recordId}
                    similarity={data.similarity}
                    content={data.content}
                    questionId={data.questionId}
                    littleQuestionId={data.littleQuestionId}
                    hideReplaceOcrResultBtn={data.hideReplaceOcrResultBtn}
                    everyAsks={data.everyAsks}
                />
            );
        case 'guessYouAsk':
            return <GuessYouAsk questions={data.questions} />;
        case 'questionSource':
            return (
                <QuestionSource
                    title={data.title}
                    content={data.content}
                    sourceType={data.sourceType}
                    videoId={data.videoId}
                    videoTime={data.videoTime}
                    questionId={data.questionId}
                    permission={data.permission}
                    url={data.url}
                />
            );
        case 'everyoneIsAsk':
            return <EveryoneIsAsk asks={data.asks} hasNextPage={data.hasNextPage} />;
        default:
            return null;
    }
};

export default memo(Respondent);
